import { AnimatePresence, motion, useMotionTemplate, useMotionValue, useSpring, useTransform } from 'framer-motion';
import Link from 'next/link';
import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import useBreakpoints from '../hooks/useBreakpoints';

const scrollYRangeForCardImageY = {
  desktop: [1000, 1500],
  tablet: [1000, 1600],
  mobile: [1000, 1900],
};

const cardImageYRange = {
  desktop: [0, 50],
  tablet: [0, 36],
  mobile: [0, 36],
};

export default function TogetherSection() {
  const media = useBreakpoints({ defaultMedia: 'desktop' });
  const containerYFromViewport = useMotionValue(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardImageX = useSpring(useTransform(containerYFromViewport, [200, 800], [12, 0]), {
    stiffness: 600,
    damping: 100,
  });

  const cardImageY = useSpring(
    useTransform(containerYFromViewport, scrollYRangeForCardImageY[media], cardImageYRange[media]),
    { stiffness: 600, damping: 100 },
  );

  const transform = useMotionTemplate`translate(${cardImageX}px, ${cardImageY}px)`;

  useEffect(() => {
    const calculateContainerY = () => {
      if (!containerRef.current) return;
      containerYFromViewport.set(window.innerHeight - containerRef.current.getBoundingClientRect().top);
    };

    calculateContainerY();
    window.addEventListener('scroll', calculateContainerY);
    window.addEventListener('resize', calculateContainerY);
    return () => {
      window.removeEventListener('resize', calculateContainerY);
      window.removeEventListener('scroll', calculateContainerY);
    };
  }, []);

  return (
    <AnimatePresence>
      <Container ref={containerRef}>
        <Wrapper>
          <TitleWrapper>
            <FakeTitle>청연과 함께하는 일상</FakeTitle>
            <TitleDesktop>{TitleDesktopSvg}</TitleDesktop>
            <TitleTablet>{TitleTabletSvg}</TitleTablet>
          </TitleWrapper>
          <CardGrid>
            <Link href="/office_clean" passHref>
              <OfficeCard>
                <OfficeCardBackground style={{ transform }}>
                  <OfficeCardFakeBack />
                  <picture>
                    <source
                      type="image/webp"
                      srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/home/webp/office_card.webp"
                    />
                    <source
                      type="image/png"
                      srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/home/office_card.png"
                    />
                    <CardBackgroundImage src="https://cleanhome-web.cleaninglab.co.kr/brandsite/home/office_card.png" />
                  </picture>
                </OfficeCardBackground>
                <CardContent>
                  <CardTitle>청연 사무실청소</CardTitle>
                  <CardDescription>합리적인 비용으로 업무 공간을 쾌적하게 관리해보세요.</CardDescription>
                  {/* <Link href="/" passHref> */}
                  <CardButton>{PlusSvg}</CardButton>
                  {/* </Link> */}
                </CardContent>
              </OfficeCard>
            </Link>
            <Link href="/store" passHref>
              <StoreCard>
                <StoreCardBackground style={{ transform }}>
                  <picture>
                    <source
                      type="image/webp"
                      srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/home/webp/store_card.webp"
                    />
                    <source
                      type="image/png"
                      srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/home/store_card.png"
                    />
                    <CardBackgroundImage src="https://cleanhome-web.cleaninglab.co.kr/brandsite/home/store_card.png" />
                  </picture>
                </StoreCardBackground>
                <CardContent>
                  <CardTitle>청연 플러스샵</CardTitle>
                  <CardDescription>청소연구소가 만든 안전하고 실용적인 생활용품을 만나보세요.</CardDescription>
                  {/* <Link href="/" passHref> */}
                  <CardButton>{PlusSvg}</CardButton>
                  {/* </Link> */}
                </CardContent>
              </StoreCard>
            </Link>
            <Link href="/moving_clean" passHref>
              <MovingCard>
                <MovingCardBackground style={{ transform }}>
                  <picture>
                    <source
                      type="image/webp"
                      srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/home/webp/moving_card.webp"
                    />
                    <source
                      type="image/png"
                      srcSet="https://cleanhome-web.cleaninglab.co.kr/brandsite/home/moving_card.png"
                    />
                    <CardBackgroundImage src="https://cleanhome-web.cleaninglab.co.kr/brandsite/home/moving_card.png" />
                  </picture>
                </MovingCardBackground>
                <CardContent>
                  <CardTitle>청연 입주청소</CardTitle>
                  <CardDescription>거품 없는 가격과 표준화된 이사/ 입주 청소를 경험해 보세요.</CardDescription>
                  {/* <Link href="/" passHref> */}
                  <CardButton>{PlusSvg}</CardButton>
                  {/* </Link> */}
                </CardContent>
              </MovingCard>
            </Link>
          </CardGrid>
        </Wrapper>
      </Container>
    </AnimatePresence>
  );
}

const Container = styled.section`
  width: 100%;
  padding: 0 24px;
  padding-top: 98px;
  padding-bottom: 120px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding-top: 86px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding-bottom: 80px;
  }
`;

const Wrapper = styled.div`
  margin: 0 auto;
  max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.desktop};

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.tablet};
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    /* max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.mobile}; */
  }
`;

const TitleWrapper = styled.div`
  margin-bottom: 64px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    margin-bottom: 40px;
  }
`;

const FakeTitle = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  margin: -1px;
  clip: rect(0, 0, 0, 0);
  text-indent: -5000px;
`;

const TitleDesktop = styled.div`
  display: block;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: none;
  }
`;

const TitleTablet = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: block;
  }
`;

const CardGrid = styled.div`
  width: 100%;
  height: 360px;
  display: grid;
  gap: 20px;
  grid-template:
    'office store gift' 1fr
    / 1fr 1fr 1fr;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    height: 510px;
    grid-template:
      'office store' 1fr
      'office gift' 1fr
      / 1fr 1fr;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    gap: 30px;
    height: 780px;
    grid-template:
      'office' 1fr
      'store' 1fr
      'gift' 1fr
      / 1fr;
  }
`;

const CardBackgroundImage = styled.img`
  position: relative;
  width: 100%;
  transform-origin: 100% 50%;
`;

const CardBackground = styled(motion.div)`
  position: absolute;
`;

const OfficeCardBackground = styled(CardBackground)`
  width: 120%;
  bottom: 0;
  right: -12px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    width: 180%;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    width: 376px;
  }
`;

const OfficeCardFakeBack = styled.div`
  position: absolute;
  width: 100%;
  height: 5%;
  background: #e8e8e8;
  right: 100%;
  bottom: 0;

  &::after {
    position: absolute;
    content: '';
    width: 100%;
    height: 78%;
    bottom: 100%;
    background: #ffffff;
  }
`;

const StoreCardBackground = styled(CardBackground)`
  width: 120%;
  bottom: 0;
  right: -12px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    width: 110%;
    bottom: -12px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    /* width: 100%; */
    bottom: -12px;
    width: 380px;
  }

  &::before {
    position: absolute;
    content: '';
    width: 200%;
    height: 25%;
    background: #e8e8e8;
    right: 0;
    bottom: 12px;
  }
`;

const MovingCardBackground = styled(CardBackground)`
  width: 120%;
  bottom: 0;
  right: -12px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    width: 110%;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    /* width: 110%;
    max-width: 500px; */
    width: 380px;
  }
`;

const CardContent = styled.dl`
  position: relative;
`;

const CardTitle = styled.dt`
  display: block;
  margin-bottom: 16px;
  font-weight: 500;
  font-size: 25px;
  line-height: 140%;
  color: rgba(0, 0, 0, 0.85);

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    font-size: 21px;
    margin-bottom: 10px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-bottom: 8px;
  }
`;

const CardDescription = styled.dd`
  font-weight: 300;
  font-size: 21px;
  line-height: 140%;
  color: rgba(0, 0, 0, 0.6);
  word-break: keep-all;
  margin-bottom: 20px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    font-size: 17px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-bottom: 14px;
    font-weight: 400;
    word-break: break-all;
  }
`;

const CardButton = styled.div`
  background: rgba(0, 0, 0, 0);
  border: 1px solid #262626;
  border-radius: 8px;
  padding: 8px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  transition: 0.2s;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding: 4px;
    border-radius: 6px;
  }

  svg path {
    transition: 0.2s;
  }
`;

const Card = styled.a`
  display: block;
  border-radius: 16px;
  padding: 32px 25px;
  background: #f5f5f5;
  position: relative;
  overflow: hidden;
  isolation: isolate;
  cursor: pointer;
  transition: 0.2s;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding: 30px 20px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    border-radius: 8px;
    padding: 20px;
  }

  &:hover {
    ${CardButton} {
      /* border: 1px solid #ffffff; */
      background: #333333;

      svg path {
        fill: #ffffff;
      }
    }

    background: rgba(174, 230, 237, 1);
  }
`;

const OfficeCard = styled(Card)`
  grid-area: office;
`;

const StoreCard = styled(Card)`
  grid-area: store;
`;

const MovingCard = styled(Card)`
  grid-area: gift;
`;

const Br = styled.br`
  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    /* display: none; */
  }
`;

const TitleDesktopSvg = (
  <svg width="396" height="45" viewBox="0 0 396 45" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M19.8351 5.214V0.843999H6.67911V5.214H19.8351ZM30.9211 35.804C30.9211 38.748 28.3451 40.588 24.5271 40.588C20.8471 40.588 18.2251 38.748 18.2251 35.804C18.2251 32.86 20.8471 31.02 24.5271 31.02C28.3451 31.02 30.9211 32.86 30.9211 35.804ZM36.6251 26.144V0.614H31.5191V13.862H24.2511V18.14H31.5191V26.144H36.6251ZM10.4971 12.206C10.4051 13.31 10.1751 14.276 9.76111 15.15C8.19711 18.692 4.65511 20.808 0.699109 22.326L3.09111 26.374C7.64511 25.04 11.9691 21.36 13.3951 18.048C14.8671 21.314 19.5131 24.304 23.5611 25.546L25.7691 21.406C21.6751 20.302 18.2711 18.14 16.8911 14.874C16.5691 14.092 16.3391 13.172 16.2471 12.206H24.5731V7.882H1.89511V12.206H10.4971ZM36.0731 35.804C36.0731 30.514 31.2891 26.696 24.5271 26.696C17.9031 26.696 13.0731 30.514 13.0731 35.804C13.0731 41.186 17.9031 44.958 24.5271 44.958C31.2891 44.958 36.0731 41.186 36.0731 35.804ZM72.8695 6.502H62.1055C60.0815 4.386 57.1375 3.052 53.6875 3.052C47.1555 3.052 42.4635 7.698 42.4635 13.632C42.4635 19.704 47.1555 24.212 53.6875 24.212C57.3675 24.212 60.4955 22.786 62.4735 20.394H72.8695V30.284H78.0215V0.705998H72.8695V6.502ZM78.6195 42.934V38.518H57.8275V27.8H52.7215V42.934H78.6195ZM59.8515 13.586C59.8515 17.082 57.1835 19.796 53.6875 19.796C50.0995 19.796 47.5235 17.082 47.5235 13.586C47.5235 10.09 50.0995 7.514 53.6875 7.514C57.1835 7.514 59.8515 10.09 59.8515 13.586ZM64.9115 13.632C64.9115 12.758 64.7735 11.838 64.5895 11.01H72.8695V15.978H64.6815C64.8195 15.242 64.9115 14.46 64.9115 13.632ZM106.354 3.098H84.6419V7.606H101.248C101.386 10.872 101.018 16.99 100.282 20.716L105.112 21.36C106.216 17.266 106.354 10.136 106.354 6.364V3.098ZM109.252 24.81C106.768 25.086 102.904 25.408 98.9939 25.684C97.7059 25.776 96.4639 25.822 95.3139 25.868V14.552H90.2079V26.052C89.6099 26.006 88.9659 26.006 88.3219 26.052C85.9759 26.052 83.7219 26.006 82.0659 26.006L82.6179 30.56H85.8379C89.2879 30.56 92.9679 30.468 96.0959 30.284C100.604 30.054 106.124 29.41 109.574 28.904L109.252 24.81ZM123.466 18.784V14.506H116.98V0.751998H111.874V39.116H116.98V18.784H123.466ZM160.965 11.424V7.146H136.493V11.424H160.965ZM154.847 4.846V0.521998H143.117V4.846H154.847ZM153.651 20.44C153.651 22.418 151.857 23.982 149.097 23.982C146.291 23.982 144.497 22.418 144.497 20.44C144.497 18.37 146.291 16.898 149.097 16.898C151.857 16.898 153.651 18.37 153.651 20.44ZM158.573 20.44C158.573 15.61 154.433 12.85 149.097 12.85C143.715 12.85 139.391 15.61 139.391 20.44C139.391 25.04 143.715 27.938 149.097 27.938C154.295 27.938 158.573 25.04 158.573 20.44ZM164.783 40.036H150.293V34.378H164.783V40.036ZM169.843 44.406V29.962H145.233V44.406H169.843ZM176.789 16.392V12.022H169.843V0.659999H164.783V27.662H169.843V16.392H176.789ZM181.707 27.846C186.123 22.694 188.975 13.678 188.745 3.466H178.855V7.698H183.731C183.731 13.494 181.339 21.084 177.521 25.27L181.707 27.846ZM210.457 39.116H215.379V0.751998H210.457V39.116ZM191.689 29.732C194.219 26.466 196.013 22.556 197.071 18.094L197.347 16.76H202.361V37.644H207.237V1.304H202.361V12.482H198.083C198.451 9.584 198.635 6.594 198.589 3.466H190.539V7.698H193.575C193.575 13.724 191.873 21.912 187.319 27.202L191.689 29.732ZM237.042 23.154C237.042 25.73 234.788 27.524 232.074 27.524C229.36 27.524 227.198 25.73 227.198 23.154C227.198 20.532 229.36 18.784 232.074 18.784C234.788 18.784 237.042 20.532 237.042 23.154ZM241.918 23.154C241.918 18.048 237.364 14.69 232.074 14.69C226.968 14.69 222.184 18.048 222.184 23.154C222.184 28.49 226.968 31.756 232.074 31.756C237.364 31.756 241.918 28.49 241.918 23.154ZM259.582 19.75V15.38H252.636V0.751998H247.576V39.07H252.636V19.75H259.582ZM243.758 12.712V8.434H219.286V12.712H243.758ZM237.64 5.858V1.488H225.91V5.858H237.64ZM299.414 25.546V21.222H260.682V25.546H299.414ZM267.214 1.12V16.622H293.526V12.206H272.366V1.12H267.214ZM266.984 29.134V43.026H293.48V38.702H272.044V29.134H266.984ZM315.569 11.884C315.569 17.496 320.215 21.912 326.701 21.912C333.325 21.912 338.017 17.496 338.017 11.884C338.017 6.088 333.325 1.81 326.701 1.81C320.215 1.81 315.569 6.088 315.569 11.884ZM320.675 11.838C320.675 8.572 323.159 6.134 326.701 6.134C330.289 6.134 332.911 8.572 332.911 11.838C332.911 15.104 330.289 17.496 326.701 17.496C323.159 17.496 320.675 15.104 320.675 11.838ZM350.391 22.418V0.659999H345.285V22.418H350.391ZM351.035 44.452V40.082H330.105V36.816H350.391V24.672H325.045V29.088H345.285V32.492H325.045V44.452H351.035ZM379.736 19.198C374.722 17.496 369.846 12.068 369.846 5.628V1.764H364.464V5.72C364.464 12.62 359.956 17.772 354.298 20.624L357.426 24.35C362.072 22.326 365.706 17.956 367.27 14.046C368.972 17.91 372.974 21.544 376.93 23.108L379.736 19.198ZM383.922 34.7C383.922 37.966 381.254 40.128 377.528 40.128C373.848 40.128 371.18 37.966 371.18 34.7C371.18 31.434 373.848 29.318 377.528 29.318C381.254 29.318 383.922 31.434 383.922 34.7ZM389.028 34.7C389.028 29.226 384.244 24.948 377.528 24.948C370.858 24.948 366.12 29.226 366.12 34.7C366.12 40.588 370.858 44.498 377.528 44.498C384.244 44.498 389.028 40.588 389.028 34.7ZM395.79 14.874V10.504H388.844V0.659999H383.738V24.58H388.844V14.874H395.79Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);

const TitleTabletSvg = (
  <svg width="239" height="27" viewBox="0 0 239 27" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.1533 3.343V0.778H4.43134V3.343H12.1533ZM18.6603 21.298C18.6603 23.026 17.1483 24.106 14.9073 24.106C12.7473 24.106 11.2083 23.026 11.2083 21.298C11.2083 19.57 12.7473 18.49 14.9073 18.49C17.1483 18.49 18.6603 19.57 18.6603 21.298ZM22.0083 15.628V0.643H19.0113V8.419H14.7453V10.93H19.0113V15.628H22.0083ZM6.67234 7.447C6.61834 8.095 6.48334 8.662 6.24034 9.175C5.32234 11.254 3.24334 12.496 0.921344 13.387L2.32534 15.763C4.99834 14.98 7.53634 12.82 8.37334 10.876C9.23734 12.793 11.9643 14.548 14.3403 15.277L15.6363 12.847C13.2333 12.199 11.2353 10.93 10.4253 9.013C10.2363 8.554 10.1013 8.014 10.0473 7.447H14.9343V4.909H1.62334V7.447H6.67234ZM21.6843 21.298C21.6843 18.193 18.8763 15.952 14.9073 15.952C11.0193 15.952 8.18434 18.193 8.18434 21.298C8.18434 24.457 11.0193 26.671 14.9073 26.671C18.8763 26.671 21.6843 24.457 21.6843 21.298ZM43.8222 4.099H37.5042C36.3162 2.857 34.5882 2.074 32.5632 2.074C28.7292 2.074 25.9752 4.801 25.9752 8.284C25.9752 11.848 28.7292 14.494 32.5632 14.494C34.7232 14.494 36.5592 13.657 37.7202 12.253H43.8222V18.058H46.8462V0.696999H43.8222V4.099ZM47.1972 25.483V22.891H34.9932V16.6H31.9962V25.483H47.1972ZM36.1812 8.257C36.1812 10.309 34.6152 11.902 32.5632 11.902C30.4572 11.902 28.9452 10.309 28.9452 8.257C28.9452 6.205 30.4572 4.693 32.5632 4.693C34.6152 4.693 36.1812 6.205 36.1812 8.257ZM39.1512 8.284C39.1512 7.771 39.0702 7.231 38.9622 6.745H43.8222V9.661H39.0162C39.0972 9.229 39.1512 8.77 39.1512 8.284ZM64.0161 2.101H51.2721V4.747H61.0191C61.1001 6.664 60.8841 10.255 60.4521 12.442L63.2871 12.82C63.9351 10.417 64.0161 6.232 64.0161 4.018V2.101ZM65.7171 14.845C64.2591 15.007 61.9911 15.196 59.6961 15.358C58.9401 15.412 58.2111 15.439 57.5361 15.466V8.824H54.5391V15.574C54.1881 15.547 53.8101 15.547 53.4321 15.574C52.0551 15.574 50.7321 15.547 49.7601 15.547L50.0841 18.22H51.9741C53.9991 18.22 56.1591 18.166 57.9951 18.058C60.6411 17.923 63.8811 17.545 65.9061 17.248L65.7171 14.845ZM74.0601 11.308V8.797H70.2531V0.723999H67.2561V23.242H70.2531V11.308H74.0601ZM97.1504 6.988V4.477H82.7864V6.988H97.1504ZM93.5594 3.127V0.588999H86.6744V3.127H93.5594ZM92.8574 12.28C92.8574 13.441 91.8044 14.359 90.1844 14.359C88.5374 14.359 87.4844 13.441 87.4844 12.28C87.4844 11.065 88.5374 10.201 90.1844 10.201C91.8044 10.201 92.8574 11.065 92.8574 12.28ZM95.7464 12.28C95.7464 9.445 93.3164 7.825 90.1844 7.825C87.0254 7.825 84.4874 9.445 84.4874 12.28C84.4874 14.98 87.0254 16.681 90.1844 16.681C93.2354 16.681 95.7464 14.98 95.7464 12.28ZM99.3914 23.782H90.8864V20.461H99.3914V23.782ZM102.361 26.347V17.869H87.9164V26.347H102.361ZM106.438 9.904V7.339H102.361V0.669999H99.3914V16.519H102.361V9.904H106.438ZM109.865 16.627C112.457 13.603 114.131 8.311 113.996 2.317H108.191V4.801H111.053C111.053 8.203 109.649 12.658 107.408 15.115L109.865 16.627ZM126.74 23.242H129.629V0.723999H126.74V23.242ZM115.724 17.734C117.209 15.817 118.262 13.522 118.883 10.903L119.045 10.12H121.988V22.378H124.85V1.048H121.988V7.609H119.477C119.693 5.908 119.801 4.153 119.774 2.317H115.049V4.801H116.831C116.831 8.338 115.832 13.144 113.159 16.249L115.724 17.734ZM142.884 13.873C142.884 15.385 141.561 16.438 139.968 16.438C138.375 16.438 137.106 15.385 137.106 13.873C137.106 12.334 138.375 11.308 139.968 11.308C141.561 11.308 142.884 12.334 142.884 13.873ZM145.746 13.873C145.746 10.876 143.073 8.905 139.968 8.905C136.971 8.905 134.163 10.876 134.163 13.873C134.163 17.005 136.971 18.922 139.968 18.922C143.073 18.922 145.746 17.005 145.746 13.873ZM156.114 11.875V9.31H152.037V0.723999H149.067V23.215H152.037V11.875H156.114ZM146.826 7.744V5.233H132.462V7.744H146.826ZM143.235 3.721V1.156H136.35V3.721H143.235ZM180.034 15.277V12.739H157.3V15.277H180.034ZM161.134 0.939999V10.039H176.578V7.447H164.158V0.939999H161.134ZM160.999 17.383V25.537H176.551V22.999H163.969V17.383H160.999ZM190.596 7.258C190.596 10.552 193.323 13.144 197.13 13.144C201.018 13.144 203.772 10.552 203.772 7.258C203.772 3.856 201.018 1.345 197.13 1.345C193.323 1.345 190.596 3.856 190.596 7.258ZM193.593 7.231C193.593 5.314 195.051 3.883 197.13 3.883C199.236 3.883 200.775 5.314 200.775 7.231C200.775 9.148 199.236 10.552 197.13 10.552C195.051 10.552 193.593 9.148 193.593 7.231ZM211.035 13.441V0.669999H208.038V13.441H211.035ZM211.413 26.374V23.809H199.128V21.892H211.035V14.764H196.158V17.356H208.038V19.354H196.158V26.374H211.413ZM228.799 11.551C225.856 10.552 222.994 7.366 222.994 3.586V1.318H219.835V3.64C219.835 7.69 217.189 10.714 213.868 12.388L215.704 14.575C218.431 13.387 220.564 10.822 221.482 8.527C222.481 10.795 224.83 12.928 227.152 13.846L228.799 11.551ZM231.256 20.65C231.256 22.567 229.69 23.836 227.503 23.836C225.343 23.836 223.777 22.567 223.777 20.65C223.777 18.733 225.343 17.491 227.503 17.491C229.69 17.491 231.256 18.733 231.256 20.65ZM234.253 20.65C234.253 17.437 231.445 14.926 227.503 14.926C223.588 14.926 220.807 17.437 220.807 20.65C220.807 24.106 223.588 26.401 227.503 26.401C231.445 26.401 234.253 24.106 234.253 20.65ZM238.222 9.013V6.448H234.145V0.669999H231.148V14.71H234.145V9.013H238.222Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);

const PlusSvg = (
  <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.23789 2.2C9.23789 1.8134 8.92449 1.5 8.53789 1.5C8.15129 1.5 7.83789 1.8134 7.83789 2.2V7.35H2.78789C2.40129 7.35 2.08789 7.6634 2.08789 8.05C2.08789 8.4366 2.40129 8.75 2.78789 8.75H7.83789V13.8C7.83789 14.1866 8.15129 14.5 8.53789 14.5C8.92449 14.5 9.23789 14.1866 9.23789 13.8V8.75H14.3879C14.7745 8.75 15.0879 8.4366 15.0879 8.05C15.0879 7.6634 14.7745 7.35 14.3879 7.35H9.23789V2.2Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);
